import { useCallback, useEffect, useState } from "react";
import { CalendarRange, Plus, Play, Send, CheckCircle2, Lock, Undo2, Unlock, XCircle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useTenant } from "@/context/TenantContext";
import { PeriodStatusBadge } from "@/components/badges";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const TRANSITIONS = {
  DRAFT: [{ to: "IN_PROGRESS", label: "Avvia rendicontazione", icon: Play, approve: false }],
  IN_PROGRESS: [
    { to: "IN_REVIEW", label: "Invia in revisione", icon: Send, approve: false },
    { to: "DRAFT", label: "Riporta a bozza", icon: Undo2, approve: false },
  ],
  IN_REVIEW: [
    { to: "APPROVED", label: "Approva", icon: CheckCircle2, approve: true },
    { to: "IN_PROGRESS", label: "Ritorna in lavorazione", icon: Undo2, approve: false },
  ],
  APPROVED: [{ to: "LOCKED", label: "Blocca esercizio", icon: Lock, approve: true }],
  LOCKED: [],
};

export default function ReportingPeriods() {
  const { companyId, periods, refreshPeriods, can } = useTenant();
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ year: new Date().getFullYear(), data_inizio: "", data_fine: "" });
  const [unlockRequests, setUnlockRequests] = useState([]);
  const [unlockTarget, setUnlockTarget] = useState(null);
  const [dataToCorrect, setDataToCorrect] = useState("");
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [reason, setReason] = useState("");
  const canManage = can("periods:manage");
  const canApprove = can("periods:approve");

  const loadRequests = useCallback(async () => {
    if (!companyId) return;
    try {
      const { data } = await api.get(`/companies/${companyId}/periods/unlock-requests`);
      setUnlockRequests(data);
    } catch {
      setUnlockRequests([]);
    }
  }, [companyId]);

  useEffect(() => { loadRequests(); }, [loadRequests]);

  const reload = useCallback(() => { refreshPeriods(); loadRequests(); }, [refreshPeriods, loadRequests]);

  const create = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        year: parseInt(form.year, 10),
        data_inizio: form.data_inizio || `${form.year}-01-01`,
        data_fine: form.data_fine || `${form.year}-12-31`,
      };
      await api.post(`/companies/${companyId}/periods`, payload);
      toast.success("Periodo creato", { description: `Esercizio ${payload.year}` });
      setCreateOpen(false);
      reload();
    } catch (err) {
      toast.error("Errore", { description: formatApiError(err) });
    }
  };

  const changeStatus = async (period, to) => {
    try {
      await api.post(`/companies/${companyId}/periods/${period.id}/status`, { status: to });
      toast.success("Stato aggiornato", { description: `Esercizio ${period.year}` });
      reload();
    } catch (err) {
      toast.error("Errore", { description: formatApiError(err) });
    }
  };

  const requestUnlock = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/companies/${companyId}/periods/${unlockTarget.id}/unlock-request`,
        { reason, data_to_correct: dataToCorrect });
      toast.success("Richiesta di sblocco inviata",
        { description: "Un autorizzatore (periods:approve) dovrà valutarla. Verrà creato uno snapshot dello stato bloccato." });
      setUnlockTarget(null);
      setReason("");
      setDataToCorrect("");
      reload();
    } catch (err) {
      toast.error("Errore", { description: formatApiError(err) });
    }
  };

  const decideUnlock = async (req, action, reasonText) => {
    try {
      const { data } = await api.post(
        `/companies/${companyId}/periods/unlock-requests/${req.id}/${action}`,
        action === "reject" ? { reason: reasonText } : undefined);
      if (action === "approve") {
        toast.success("Periodo riaperto", {
          description: `Snapshot ${data.snapshot_id?.slice(0, 8)}… creato. ${data.reports_marked_stale} output marcati da rigenerare.`,
        });
      } else {
        toast.success("Richiesta di sblocco rifiutata");
        setRejectTarget(null);
        setRejectReason("");
      }
      reload();
    } catch (err) {
      toast.error("Errore", { description: formatApiError(err) });
    }
  };

  const fmt = (iso) => (iso ? new Date(iso).toLocaleString("it-IT") : "—");
  const pendingByPeriod = Object.fromEntries(
    unlockRequests.filter((r) => r.status === "PENDING").map((r) => [r.period_id, r]));

  return (
    <div className="space-y-6" data-testid="reporting-periods-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-3">
            <CalendarRange className="w-7 h-7 text-green-700" aria-hidden="true" />
            Periodi di rendicontazione ESG
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Ogni dato ESG appartiene a uno specifico esercizio. Stati: Bozza → In corso → In revisione → Approvato → Bloccato.
            La riapertura di un esercizio bloccato richiede una richiesta motivata e un'autorizzazione separata.
          </p>
        </div>
        {canManage && (
          <Button className="bg-green-700 hover:bg-green-800" onClick={() => setCreateOpen(true)} data-testid="create-period-button">
            <Plus className="w-4 h-4 mr-2" aria-hidden="true" /> Nuovo periodo
          </Button>
        )}
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-0 overflow-x-auto">
          <Table data-testid="periods-table">
            <TableHeader>
              <TableRow>
                <TableHead>Esercizio</TableHead>
                <TableHead>Data inizio</TableHead>
                <TableHead>Data fine</TableHead>
                <TableHead>Stato</TableHead>
                <TableHead>Creato</TableHead>
                <TableHead>Ultima modifica</TableHead>
                <TableHead>Cronologia</TableHead>
                <TableHead className="text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {periods.map((p) => {
                const pending = pendingByPeriod[p.id];
                return (
                  <TableRow key={p.id} data-testid={`period-row-${p.year}`}>
                    <TableCell className="font-semibold">{p.year}</TableCell>
                    <TableCell>{p.data_inizio}</TableCell>
                    <TableCell>{p.data_fine}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <PeriodStatusBadge status={p.status} />
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-600 text-sm">{fmt(p.created_at)}</TableCell>
                    <TableCell className="text-slate-600 text-sm">{fmt(p.updated_at)}</TableCell>
                    <TableCell className="text-slate-600 text-sm" data-testid={`period-${p.year}-history`}>
                      {p.approved_at && <p>Approvato: {fmt(p.approved_at)}</p>}
                      {p.reopened_at && <p>Riaperto: {fmt(p.reopened_at)}</p>}
                      {!p.approved_at && !p.reopened_at && "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1.5 flex-wrap">
                        {(TRANSITIONS[p.status] || [])
                          .filter((t) => (t.approve ? canApprove : canManage))
                          .map((t) => (
                            <Button key={t.to} size="sm" variant="outline"
                              onClick={() => changeStatus(p, t.to)}
                              data-testid={`period-${p.year}-action-${t.to.toLowerCase()}`}>
                              <t.icon className="w-3.5 h-3.5 mr-1" aria-hidden="true" /> {t.label}
                            </Button>
                          ))}
                        {p.status === "LOCKED" && !pending && canManage && (
                          <Button size="sm" variant="outline"
                            onClick={() => { setUnlockTarget(p); setReason(""); setDataToCorrect(""); }}
                            data-testid={`period-${p.year}-action-unlock-request`}>
                            <Unlock className="w-3.5 h-3.5 mr-1" aria-hidden="true" /> Richiedi sblocco
                          </Button>
                        )}
                        {p.status === "LOCKED" && pending && (
                          canApprove ? (
                            <>
                              <Button size="sm" variant="outline"
                                className="border-amber-300 text-amber-800"
                                onClick={() => decideUnlock(pending, "approve")}
                                data-testid={`period-${p.year}-action-unlock-approve`}>
                                <Unlock className="w-3.5 h-3.5 mr-1" aria-hidden="true" /> Autorizza sblocco
                              </Button>
                              <Button size="sm" variant="outline"
                                onClick={() => { setRejectTarget(pending); setRejectReason(""); }}
                                data-testid={`period-${p.year}-action-unlock-reject`}>
                                <XCircle className="w-3.5 h-3.5 mr-1" aria-hidden="true" /> Rifiuta
                              </Button>
                            </>
                          ) : (
                            <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-300 text-[10px]"
                              data-testid={`period-${p.year}-unlock-pending`}>Sblocco in attesa</Badge>
                          )
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="create-period-dialog">
          <DialogHeader><DialogTitle>Nuovo periodo di rendicontazione</DialogTitle></DialogHeader>
          <form onSubmit={create} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="p-year">Anno *</Label>
              <Input id="p-year" type="number" min="2000" max="2100" required value={form.year}
                onChange={(e) => setForm({ ...form, year: e.target.value })} data-testid="period-year-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="p-start">Data inizio</Label>
                <Input id="p-start" type="date" value={form.data_inizio}
                  onChange={(e) => setForm({ ...form, data_inizio: e.target.value })} data-testid="period-start-input" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="p-end">Data fine</Label>
                <Input id="p-end" type="date" value={form.data_fine}
                  onChange={(e) => setForm({ ...form, data_fine: e.target.value })} data-testid="period-end-input" />
              </div>
            </div>
            <p className="text-xs text-slate-500">Se le date sono vuote verrà usato l'anno solare (01/01 – 31/12).</p>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)} data-testid="period-cancel">Annulla</Button>
              <Button type="submit" className="bg-green-700 hover:bg-green-800" data-testid="period-submit">Crea periodo</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!unlockTarget} onOpenChange={(o) => { if (!o) setUnlockTarget(null); }}>
        <DialogContent data-testid="unlock-request-dialog">
          <DialogHeader><DialogTitle>Richiedi sblocco esercizio {unlockTarget?.year}</DialogTitle></DialogHeader>
          <form onSubmit={requestUnlock} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="unlock-reason">Motivazione *</Label>
              <Input id="unlock-reason" required minLength={10} value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Es. errore materiale nei consumi energetici comunicato dal fornitore"
                data-testid="unlock-reason-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="unlock-data-to-correct">Dati da correggere *</Label>
              <Input id="unlock-data-to-correct" required minLength={5} value={dataToCorrect}
                onChange={(e) => setDataToCorrect(e.target.value)}
                placeholder="Es. consumi elettrici Q3, fattore di emissione gas"
                data-testid="unlock-data-to-correct-input" />
            </div>
            <p className="text-xs text-slate-500">
              All'autorizzazione verrà creato uno snapshot completo dei dati bloccati (valori canonici e output)
              e gli output generati saranno marcati come da rigenerare. Ogni passaggio è registrato in audit.
            </p>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setUnlockTarget(null)}
                data-testid="unlock-cancel">Annulla</Button>
              <Button type="submit" className="bg-amber-600 hover:bg-amber-700"
                data-testid="unlock-submit">Invia richiesta</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!rejectTarget} onOpenChange={(o) => { if (!o) setRejectTarget(null); }}>
        <DialogContent data-testid="unlock-reject-dialog">
          <DialogHeader><DialogTitle>Rifiuta richiesta di sblocco</DialogTitle></DialogHeader>
          <form onSubmit={(e) => { e.preventDefault(); decideUnlock(rejectTarget, "reject", rejectReason); }} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="reject-reason">Motivazione del rigetto *</Label>
              <Input id="reject-reason" required minLength={10} value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Es. correzione non sufficientemente motivata"
                data-testid="reject-reason-input" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setRejectTarget(null)}
                data-testid="reject-cancel">Annulla</Button>
              <Button type="submit" className="bg-red-700 hover:bg-red-800"
                data-testid="reject-submit">Rifiuta richiesta</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
